// ===========================================
// Lesson Note Item Component
// ===========================================

import { Box, Paper, Typography } from '@mui/material';
import { format } from 'date-fns';
import { Note } from '../../../api/notes.api';
import NoteContent from '../../notes/NoteContent';
import NoteVisibilityChip from './NoteVisibilityChip';

interface LessonNoteItemProps {
  note: Note;
  showFullContent?: boolean;
}

export default function LessonNoteItem({ note, showFullContent = false }: LessonNoteItemProps) {
  const isStudentNote = !!note.studentId;
  // A student note whose Student relation is missing must not render
  // "undefined undefined" — a template literal stringifies undefined, so the
  // name is built only when the relation is actually present.
  const studentName = note.student
    ? `${note.student.firstName} ${note.student.lastName}`.trim()
    : '';
  const authorName = note.author
    ? `${note.author.firstName} ${note.author.lastName}`.trim()
    : '';

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2">
            {isStudentNote ? studentName || 'Student Note' : 'Class Note'}
          </Typography>
          <NoteVisibilityChip visibility={note.visibility} />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {format(new Date(note.createdAt), 'd MMM yyyy')}
        </Typography>
      </Box>
      <NoteContent content={note.content} showFullContent={showFullContent} />
      {authorName && (
        <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
          By {authorName}
        </Typography>
      )}
    </Paper>
  );
}
